"use client";

import { c as _c } from "react/compiler-runtime";
import { useRouter } from "@/i18n/navigation";
import { hasPendingOverwolfToken } from "@/lib/overwolf-link";
import { useT, useGameLocale } from "@/lib/i18n";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/app/contexts/AuthContext";
import { useToast } from "@/app/components/Toast";
import RunDropZone from "@/app/components/RunDropZone";
import ProfileStats, { type StatsTab } from "@/app/components/ProfileStats";
import MyChartsSection from "@/app/components/MyChartsSection";
import { EMPTY_INSIGHT_FILTERS, InsightsFilterBar, type InsightFilters } from "@/app/components/ProfileInsights";
import YourStats from "@/app/[locale]/players/[username]/YourStats";
import { parseQuery, queryParams } from "@/lib/run-query";
const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
interface Run {
  run_hash: string;
  character: string;
  win: boolean;
  was_abandoned: boolean;
  ascension: number;
  game_mode: string;
  player_count: number;
  floors_reached: number;
  killed_by: string | null;
  username: string | null;
  submitted_at: string;
}
interface UploadResult {
  filename: string;
  status: "claimed" | "duplicate" | "error";
  detail?: string;
  run_hash?: string;
}
export default function ProfileClient() {
  const {
    user,
    loading
  } = useAuth();
  const router = useRouter();
  useEffect(() => {
    if (user && hasPendingOverwolfToken()) router.replace("/link/overwolf");
  }, [user, router]);
  const lang = useGameLocale();
  const t = useT();
  const {
    toast
  } = useToast();
  const [runs, setRuns] = useState<Run[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [runsQuery, setRunsQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [versions, setVersions] = useState<string[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<UploadResult[] | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{
    total: number;
    done: number;
    dupes: number;
    errors: number;
  } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  useEffect(() => {
    const id = setTimeout(() => {
      setAppliedQuery(runsQuery);
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [runsQuery]);
  useEffect(() => {
    if (!parseQuery(appliedQuery).filters.version || versions.length) return;
    fetch(`${API_BASE}/api/runs/versions`).then(r => r.ok ? r.json() : {
      versions: []
    }).then(d => setVersions((d.versions || []).filter((v: string) => !v.toLowerCase().includes("nonreleased")).sort((a: string, b: string) => b.localeCompare(a, undefined, {
      numeric: true
    })))).catch(() => {});
  }, [appliedQuery, versions.length]);
  const fetchRuns = useCallback(async (p: number) => {
    setRunsLoading(true);
    try {
      const params = queryParams(parseQuery(appliedQuery).filters, versions);
      params.set("page", String(p));
      params.set("limit", "20");
      const res = await fetch(`${API_BASE}/api/auth/runs?${params}`, {
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setRuns(data.runs || []);
        setTotal(data.total || 0);
      }
    } catch {
      toast(t("Failed to load runs"), "error");
    } finally {
      setRunsLoading(false);
    }
  }, [toast, lang, appliedQuery, versions]);
  useEffect(() => {
    if (user) fetchRuns(page);
  }, [user, page, fetchRuns]);

  // Batches go up in chunks of 10, sequentially: one request for 100
  // files was a single point of failure (a proxy body cap or a dropped
  // connection lost the whole batch), and 10 chunks a minute is exactly
  // the endpoint's rate limit. A failed chunk is retried twice before its
  // files are reported as errors and the rest keep going.
  const UPLOAD_CHUNK = 10;
  const handleUpload = async (files: FileList | File[]) => {
    const all = Array.from(files);
    if (!all.length) return;
    setUploading(true);
    setUploadResults(null);
    const progress = {
      total: all.length,
      done: 0,
      dupes: 0,
      errors: 0
    };
    setUploadProgress({
      ...progress
    });
    const results: UploadResult[] = [];
    const summary = {
      claimed: 0,
      duplicates: 0,
      errors: 0
    };
    let signedOut = false;
    const send = async (chunk: File[]): Promise<Response> => {
      const formData = new FormData();
      chunk.forEach(f => formData.append("files", f));
      return fetch(`${API_BASE}/api/auth/runs/upload`, {
        method: "POST",
        credentials: "include",
        body: formData
      });
    };
    for (let i = 0; i < all.length && !signedOut; i += UPLOAD_CHUNK) {
      const chunk_0 = all.slice(i, i + UPLOAD_CHUNK);
      let res_0: Response | null = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          res_0 = await send(chunk_0);
        } catch {
          res_0 = null;
        }
        // Retry only what a retry can fix: network drops and 5xx.
        if (res_0 && (res_0.ok || res_0.status < 500)) break;
        await new Promise(r_0 => setTimeout(r_0, 1500 * (attempt + 1)));
      }
      if (res_0?.ok) {
        const data_0 = await res_0.json();
        results.push(...(data_0.results || []));
        summary.claimed += data_0.summary?.claimed || 0;
        summary.duplicates += data_0.summary?.duplicates || 0;
        summary.errors += data_0.summary?.errors || 0;
      } else if (res_0?.status === 401) {
        signedOut = true;
      } else {
        const err = res_0 ? await res_0.json().catch(() => null) : null;
        const detail = res_0?.status === 413 ? t("Too many files or file too large") : err?.detail || (res_0 ? t("Upload failed") : t("Network error during upload"));
        chunk_0.forEach(f_0 => results.push({
          filename: f_0.name,
          status: "error",
          detail
        }));
        summary.errors += chunk_0.length;
      }
      progress.done = Math.min(all.length, i + chunk_0.length);
      progress.dupes = summary.duplicates;
      progress.errors = summary.errors;
      setUploadProgress({
        ...progress
      });
      setUploadResults([...results]);
    }
    if (signedOut) {
      toast(t("Please sign in to upload runs"), "error");
    } else {
      toast(`${summary.claimed} ${t("claimed")}, ${summary.duplicates} ${t("duplicates")}, ${summary.errors} ${t("errors")}`, summary.errors > 0 ? "error" : "success");
      if (summary.claimed > 0) {
        fetchRuns(1);
        setPage(1);
      }
    }
    setUploadProgress(null);
    setUploading(false);
  };
  const handleDelete = async (runHash: string) => {
    try {
      const res_1 = await fetch(`${API_BASE}/api/auth/runs/${runHash}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (res_1.ok) {
        toast(t("Run removed from your profile"), "success");
        setRuns(prev => prev.filter(r_1 => r_1.run_hash !== runHash));
        setTotal(prev_0 => prev_0 - 1);
      } else if (res_1.status === 403) {
        toast(t("You do not own this run"), "error");
      } else if (res_1.status === 404) {
        toast(t("Run not found"), "error");
      } else {
        toast(t("Failed to delete run"), "error");
      }
    } catch {
      toast(t("Network error"), "error");
    } finally {
      setDeleteConfirm(null);
    }
  };
  const handleDeleteMany = async (hashes: string[]) => {
    try {
      const res_2 = await fetch(`${API_BASE}/api/auth/runs/bulk-delete`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          run_hashes: hashes
        })
      });
      if (!res_2.ok) {
        toast(t("Failed to delete run"), "error");
        return;
      }
      const body = (await res_2.json()) as {
        deleted: string[];
        failed: Record<string, string>;
      };
      const removed = new Set(body.deleted);
      if (removed.size > 0) {
        // Count what this update actually drops rather than what the server
        // reported, so a row a single delete already removed is not subtracted
        // from the total twice.
        setRuns(prev_1 => {
          const next = prev_1.filter(r_2 => !removed.has(r_2.run_hash));
          const dropped = prev_1.length - next.length;
          if (dropped > 0) setTotal(current => Math.max(0, current - dropped));
          return next;
        });
      }
      const failedCount = Object.keys(body.failed ?? {}).length;
      if (failedCount > 0) {
        toast(t("Removed {n} runs, {failed} could not be removed", {
          n: removed.size,
          failed: failedCount
        }), "error");
      } else {
        toast(t("Removed {n} runs from your profile", {
          n: removed.size
        }), "success");
      }
    } catch {
      toast(t("Network error"), "error");
    }
  };
  if (loading) {
    return <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="h-8 w-48 bg-[var(--bg-card)] rounded animate-pulse" />
      </div>;
  }
  if (!user) {
    return <div className="max-w-5xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-4">
          {t("Sign in to view your profile")}
        </h1>
        <p className="text-[var(--text-secondary)]">
          {t("Connect your Steam or Discord account to see your runs and stats.")}
        </p>
      </div>;
  }
  const totalPages = Math.ceil(total / 20);
  return <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
      <h1 className="text-2xl font-bold text-[var(--text-primary)]">
        {user.username ? `${user.username}'s ${t("Profile")}` : t("Your Profile")}
      </h1>

      {/* Stats (includes My Runs as a tab) */}
      <section>
        <ProfileStats runs={runs} runsTotal={total} runsLoading={runsLoading} runsPage={page} runsTotalPages={totalPages} onPageChange={setPage} onDeleteRun={handleDelete} deleteConfirm={deleteConfirm} onDeleteConfirm={setDeleteConfirm} onDeleteRuns={handleDeleteMany} runsQuery={runsQuery} onRunsQueryChange={setRunsQuery} overviewExtra={user.username ? <OwnStats key={user.user_id} username={user.username} /> : null} statsTab={tab => user.username ? <OwnStats key={`${user.user_id}-${tab}`} username={user.username} tab={tab} /> : null} chartsTab={<MyChartsSection />} />
      </section>

      {/* Claim Runs */}
      <section>
        <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">
          {t("Claim Runs")}
        </h2>
        <RunDropZone onFiles={files_0 => handleUpload(files_0)} uploading={uploading} uploadProgress={uploadProgress} />

        {uploadResults && uploadResults.length > 0 && <div className="mt-3 space-y-1 max-h-40 overflow-y-auto">
            {uploadResults.map((r_3, i_0) => <div key={i_0} className={`text-xs px-3 py-1.5 rounded flex items-center justify-between ${r_3.status === "claimed" ? "bg-success/10 text-success" : r_3.status === "duplicate" ? "bg-warning/10 text-warning" : "bg-danger/10 text-danger"}`}>
                <span className="truncate">{r_3.filename}</span>
                <span className="shrink-0 ml-2">
                  {r_3.status === "error" ? r_3.detail : r_3.status}
                </span>
              </div>)}
          </div>}
      </section>
    </div>;
}
function OwnStats(t0) {
  const $ = _c(10);
  const {
    username,
    tab
  } = t0;
  const lang = useGameLocale();
  const [filters, setFilters] = useState(EMPTY_INSIGHT_FILTERS);
  let t1;
  if ($[0] !== filters || $[1] !== lang) {
    t1 = <div className="flex justify-end"><InsightsFilterBar value={filters} onChange={setFilters} lang={lang} /></div>;
    $[0] = filters;
    $[1] = lang;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== filters || $[4] !== tab || $[5] !== username) {
    t2 = <YourStats username={username} filters={filters} own={true} tab={tab} />;
    $[3] = filters;
    $[4] = tab;
    $[5] = username;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <div className="space-y-3">{t1}{t2}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}